import { Injectable } from '@angular/core';
import { deleteProperty } from '@models/apollo';
import {
  AssignTrainingInput,
  AssignTrainingMutation,
  CreateInviteeInput,
  CreateInviteeMutation,
  Learner,
  LoadLearnerCertificationsQuery,
  LoadLearnerDetailsQuery,
  LoadLearnerGrantDetailsQuery,
  LoadLearnerInvitesQuery,
  LoadLearnerListQuery,
  LoadLearnerListQueryVariables,
  LoginInviteInput,
  LoginInviteMutation,
  ShareLearnersInput,
  ShareLearnersMutation,
  UpdateLearnerInput,
  UpdateLearnerMutation,
  UpdateLearnersActiveInput,
  UpdateLearnersActiveMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LearnersService {
  constructor(
    private loadLearnerListQuery: LoadLearnerListQuery,
    private assignTrainingMutation: AssignTrainingMutation,
    private createInviteeMutation: CreateInviteeMutation,
    private loadLearnerDetailsQuery: LoadLearnerDetailsQuery,
    private loadLearnerGrantDetailsQuery: LoadLearnerGrantDetailsQuery,
    private loadLearnerInvitesQuery: LoadLearnerInvitesQuery,
    private loadLearnerCertificationsQuery: LoadLearnerCertificationsQuery,
    private updateLearnersActiveMutation: UpdateLearnersActiveMutation,
    private updateLearnerMutation: UpdateLearnerMutation,
    private shareLearnersMutation: ShareLearnersMutation,
    private loginInviteMutation: LoginInviteMutation
  ) {}

  list = (vars?: LoadLearnerListQueryVariables) => {
    const variables = { first: 10_000, ...vars };
    const query = this.loadLearnerListQuery.watch(variables);

    return query.valueChanges.pipe(
      map(({ data }) => data.learners.all),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          query.fetchMore({ variables: { ...variables, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  };

  assignTraining(input: AssignTrainingInput) {
    return this.assignTrainingMutation
      .mutate(
        { input },
        {
          refetchQueries: ['LoadLicenseGroupDetails', 'LoadLicenseGroupList', 'LoadLicenseInvites', 'LoadLicenseUsage']
        }
      )
      .pipe(
        map(({ data }) => data.assignTraining),
        finalMap('invite')
      );
  }

  create(input: CreateInviteeInput) {
    return this.createInviteeMutation
      .mutate(
        { input },
        {
          update: (store) => {
            // This clears the cache
            store.modify({
              id: 'LearnersNamespace:{}',
              fields: {
                all: deleteProperty()
              }
            });
          }
        }
      )
      .pipe(
        map(({ data }) => data.createInvitee),
        map(({ learner, errors }) => {
          if (errors.length) {
            errors = errors.map((error) => {
              if (/phone|email/i.test(error.message)) {
                return { ...error, detail: input };
              }

              return error;
            });

            throw errors;
          }

          return learner as Learner;
        })
      );
  }

  find = (id: string) => {
    return this.loadLearnerDetailsQuery.watch({ id }).valueChanges.pipe(map(({ data }) => data.learners.find));
  };

  loadGrants = (id: string) => {
    return this.loadLearnerGrantDetailsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.learners.find.grants));
  };

  loadInvites = (id: string) => {
    return this.loadLearnerInvitesQuery.watch({ id }).valueChanges.pipe(map(({ data }) => data.learners.find.invites));
  };

  loadCertifications = (id: string) => {
    return this.loadLearnerCertificationsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.learners.find.certifications));
  };

  updateActive(input: UpdateLearnersActiveInput) {
    return this.updateLearnersActiveMutation
      .mutate({ input })
      .pipe(map(({ data }) => data.updateLearnersActive.learners));
  }

  update(input: UpdateLearnerInput) {
    return this.updateLearnerMutation.mutate({ input }).pipe(
      map(({ data }) => data.updateLearner),
      finalMap('learner')
    );
  }

  share(input: ShareLearnersInput) {
    return this.shareLearnersMutation
      .mutate(
        { input },
        {
          update: (store) => {
            // This clears the cache
            store.modify({
              id: 'LearnersNamespace:{}',
              fields: {
                all: deleteProperty()
              }
            });
          }
        }
      )
      .pipe(map(({ data }) => data.shareLearners.learners));
  }

  loginInvite(input: LoginInviteInput) {
    return this.loginInviteMutation.mutate({ input }).pipe(map(({ data }) => data.loginInvite.ok));
  }
}
